import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Mail, Phone, MapPin, Pencil, CheckCircle2, Calendar, Star, UserRound,
  Building2, Clock, Cake, Briefcase, Wrench, FileText, BarChart3, MoreHorizontal,
  ClipboardList,
} from 'lucide-react';

import { useEmployeeStore } from '../../../store/employeeStore';
import { useTicketStore } from '../../../store/ticketStore';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/Card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/Avatar';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { Separator } from '../../../components/Separator';
import { EmptyState } from '../../../components/EmptyState';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../../components/Tabs';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../../components/DropdownMenu';
import { LoadingPage } from '../../../components/Loading';
import { EmployeeHoldingsPanel } from '../../../components/assets/EmployeeAssets';
import { PersonalDetailsForm } from '../../../components/PersonalDetailsForm';
import { useProfileStore, type EmployeeProfile } from '../../../store/profileStore';
import { useRbacStore } from '../../../store/rbacStore';

/** Personal details for an employee — admins edit, reporting managers read. */
function ProfileTab({ employeeId }: { employeeId: string }) {
  const { getForEmployee, saveForEmployee } = useProfileStore();
  const canEdit = useRbacStore((s) => s.hasPermission('People', 'Employees', 'Edit'));
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let c = false;
    getForEmployee(employeeId).then((p) => { if (!c) setProfile(p); }).catch(() => undefined).finally(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, [employeeId, getForEmployee]);
  const save = async (patch: Record<string, any>) => { const u = await saveForEmployee(employeeId, patch); setProfile(u); return u; };
  if (loading) return <div className="py-8 text-sm text-muted-foreground">Loading details…</div>;
  if (!profile) return <div className="py-8 text-sm text-muted-foreground">No details available.</div>;
  return <PersonalDetailsForm profile={profile} canEdit={canEdit} onSave={save} />;
}

const statusMeta: Record<string, { label: string; variant: any }> = {
  active: { label: 'Active', variant: 'success' },
  'on-leave': { label: 'On leave', variant: 'warning' },
  terminated: { label: 'Terminated', variant: 'destructive' },
  resigned: { label: 'Resigned', variant: 'muted' },
};

const employmentTypeLabels: Record<string, string> = {
  'full-time': 'Regular (Full-time)',
  'regular': 'Regular (Full-time)',
  'contract': 'Contract Employee',
  'freelance': 'Freelancer',
  'part-time': 'Part-time',
  'intern': 'Intern',
  'consultant': 'Consultant',
};

function fmtDate(value?: string, pattern = 'dd MMM yyyy') {
  if (!value) return '—';
  try {
    return format(parseISO(value), pattern);
  } catch {
    return value;
  }
}

export function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentEmployee, fetchEmployee, isLoading } = useEmployeeStore();

  useEffect(() => {
    if (id) fetchEmployee(id);
  }, [id, fetchEmployee]);

  if (isLoading || !currentEmployee) {
    return <LoadingPage message="Loading employee profile…" />;
  }

  const e = currentEmployee;
  const status = statusMeta[e.status] ?? { label: e.status, variant: 'muted' };
  const empTypeLabel = employmentTypeLabels[e.employmentType || 'full-time'] || e.employmentType || 'Regular';
  const roleLabel = (e as any).role || (e as any).userRole || 'Employee';
  const fullName = `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim();
  const init =
    ((e.firstName?.[0] ?? e.displayName?.[0] ?? '?') + (e.lastName?.[0] ?? '')).toUpperCase();

  const contact = [
    { icon: Mail, value: e.email || '—' },
    { icon: Phone, value: e.phone || 'Not provided' },
    { icon: MapPin, value: e.location || 'Remote' },
  ];

  const quickInfo = [
    { label: 'Joined', value: fmtDate(e.joinDate), icon: Calendar },
    { label: 'Employee ID', value: e.employeeCode || '—', icon: UserRound },
    { label: 'Employment Type', value: empTypeLabel, icon: Briefcase },
    { label: 'System Role', value: roleLabel, icon: UserRound },
    { label: 'Location', value: e.location || 'BLR Office', icon: Building2 },
    { label: 'Timezone', value: 'IST (UTC+5:30)', icon: Clock },
  ];

  return (
    <div>
      <Breadcrumbs
        className="mb-4"
        items={[
          { label: 'People', href: '/people' },
          { label: 'Employees', href: '/people/employees' },
          { label: fullName || 'Employee' },
        ]}
      />

      {/* Profile header */}
      <Card className="mb-6 overflow-hidden">
        <div className="h-20" />
        <CardContent className="-mt-10 p-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <Avatar size="xl" className="size-20 ring-4 ring-card">
                {e.avatar && <AvatarImage src={e.avatar} alt={fullName} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {init}
                </AvatarFallback>
              </Avatar>
              <div className="pb-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="tracking-tight text-foreground font-display text-page-title">
                    {fullName}
                  </h1>
                  <Badge variant={status.variant} dot>{status.label}</Badge>
                  <Badge variant="outline">{empTypeLabel}</Badge>
                  {roleLabel && <Badge variant="secondary">{roleLabel}</Badge>}
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {e.designation || '—'}
                  {e.department ? ` · ${e.department}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(`/people/employees/${id}/edit`)}>
                <Pencil /> Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem><Mail className="size-4" /> Send email</DropdownMenuItem>
                  <DropdownMenuItem><Calendar className="size-4" /> Schedule 1:1</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Separator className="my-5" />

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            {contact.map(({ icon: Icon, value }, i) => (
              <div key={i} className="flex items-center gap-2 text-muted-foreground">
                <Icon className="size-4" />
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList variant="underline" className="w-full overflow-x-auto">
          <TabsTrigger variant="underline" value="overview">Overview</TabsTrigger>
          <TabsTrigger variant="underline" value="details">Details</TabsTrigger>
          <TabsTrigger variant="underline" value="career">Career</TabsTrigger>
          <TabsTrigger variant="underline" value="skills">Skills</TabsTrigger>
          <TabsTrigger variant="underline" value="performance">Performance</TabsTrigger>
          <TabsTrigger variant="underline" value="assets">Assets</TabsTrigger>
          <TabsTrigger variant="underline" value="documents">Documents</TabsTrigger>
          <TabsTrigger variant="underline" value="requests">Requests</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Quick info</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
                  {quickInfo.map((info) => (
                    <div key={info.label}>
                      <dt className="mb-1 text-xs font-medium text-muted-foreground">
                        {info.label}
                      </dt>
                      <dd className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <info.icon className="size-4 text-muted-foreground" />
                        {info.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-4">
                  {[
                    { dot: 'bg-primary', title: 'Project Beta launched', time: '2 days ago' },
                    { dot: 'bg-success', title: 'Performance review completed', time: '1 week ago' },
                    { dot: 'bg-warning', title: 'Completed security training', time: '3 weeks ago' },
                  ].map((a, i) => (
                    <li key={i} className="flex gap-3">
                      <span className={`mt-1.5 size-2 shrink-0 rounded-full ${a.dot}`} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{a.title}</p>
                        <p className="text-xs text-muted-foreground">{a.time}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Career */}
        <TabsContent value="details">
          {id && <ProfileTab employeeId={id} />}
        </TabsContent>

        <TabsContent value="career">
          <Card>
            <CardHeader><CardTitle>Career timeline</CardTitle></CardHeader>
            <CardContent>
              {e.career?.length ? (
                <ol className="relative ml-2 space-y-8 border-l border-border pl-8">
                  {e.career.map((entry: any) => (
                    <li key={entry.id} className="relative">
                      <span className="absolute -left-[37px] top-0.5 size-3.5 rounded-full border-2 border-primary bg-card ring-4 ring-primary/10" />
                      <h3 className="text-foreground font-display text-card-title">{entry.title}</h3>
                      <p className="text-sm font-medium text-primary">{entry.department}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {fmtDate(entry.start_date, 'MMM yyyy')} —{' '}
                        {entry.end_date ? fmtDate(entry.end_date, 'MMM yyyy') : 'Present'}
                      </p>
                      {entry.notes && (
                        <p className="mt-2 text-sm text-muted-foreground">{entry.notes}</p>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <EmptyState icon={<Briefcase />} title="No career history" description="Career milestones will appear here." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skills */}
        <TabsContent value="skills">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Skills & proficiencies</CardTitle>
              <Button variant="outline" size="sm">Add skill</Button>
            </CardHeader>
            <CardContent>
              {e.skills?.length ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {e.skills.map((skill: any) => {
                    const pct = skill.proficiency_level === 'expert' ? 90
                      : skill.proficiency_level === 'advanced' ? 75 : 50;
                    return (
                      <div key={skill.id} className="rounded-lg border border-border bg-surface-1 p-4 transition-colors hover:border-primary/40">
                        <div className="mb-3 flex items-start justify-between">
                          <div>
                            <h3 className="text-foreground font-display text-card-title">{skill.name}</h3>
                            <p className="text-xs text-muted-foreground">{skill.category}</p>
                          </div>
                          {skill.verified && <CheckCircle2 className="size-4 text-success" />}
                        </div>
                        <div className="mb-1.5 flex justify-between text-xs">
                          <span className="capitalize text-muted-foreground">{skill.proficiency_level}</span>
                          <span className="font-medium text-primary">{skill.years_of_experience} yrs</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState icon={<Wrench />} title="No skills listed" description="Add skills to build this profile." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assets — live from the Asset Hub (assets.assets + licence seats) */}
        <TabsContent value="assets">
          <EmployeeHoldingsPanel employeeId={currentEmployee.id} />
        </TabsContent>

        {(['performance', 'documents'] as const).map((t) => (
          <TabsContent key={t} value={t}>
            <Card>
              <CardContent className="py-4">
                <EmptyState
                  icon={t === 'performance' ? <BarChart3 /> : <FileText />}
                  title={`${t[0].toUpperCase()}${t.slice(1)} coming soon`}
                  description="Detailed implementation for this module is in progress."
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* Requests — live tickets and module requests linked to this employee */}
        <TabsContent value="requests">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle>Linked Requests &amp; Tickets</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  All requests and tickets submitted by or logged for this employee across all departments
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => navigate('/support/tickets')}>
                Raise Request
              </Button>
            </CardHeader>
            <CardContent>
              {(() => {
                const { tickets } = useTicketStore.getState();
                const empEmail = (currentEmployee.email || '').toLowerCase();
                const empName = `${currentEmployee.firstName} ${currentEmployee.lastName}`.toLowerCase();
                const linked = tickets.filter((t) => {
                  const reqEmail = (t.requesterEmail || '').toLowerCase();
                  const reqName = (t.requesterName || '').toLowerCase();
                  return (
                    (empEmail && reqEmail === empEmail) ||
                    (empName && reqName.includes(empName)) ||
                    t.requesterId === currentEmployee.id
                  );
                });

                if (linked.length === 0) {
                  return (
                    <EmptyState
                      icon={<ClipboardList />}
                      title="No requests on record"
                      description="Requests and support tickets associated with this employee will appear here."
                    />
                  );
                }

                return (
                  <div className="space-y-3">
                    {linked.map((t) => (
                      <div
                        key={t.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border border-border bg-card hover:border-primary/50 transition-all cursor-pointer"
                        onClick={() => navigate('/support/tickets')}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-primary">{t.ticketId || t.ticketNumber}</span>
                            <Badge variant="secondary" className="text-[10px] font-mono uppercase">
                              {t.departmentQueue || t.teamKey || 'SUPPORT'} QUEUE
                            </Badge>
                            {t.originModule && (
                              <Badge variant="outline" className="text-[10px] font-mono uppercase">
                                {t.originModule}
                              </Badge>
                            )}
                          </div>
                          <h4 className="text-xs font-semibold text-foreground">{t.subject}</h4>
                          <span className="text-[11px] text-muted-foreground block">
                            Submitted {new Date(t.createdAt).toLocaleDateString()} · Channel: {t.source || 'Portal'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge
                            variant={['resolved', 'closed'].includes(t.status?.toLowerCase()) ? 'success' : 'primary'}
                            className="capitalize text-[10px]"
                          >
                            {t.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
